"use client"

import type React from "react"
import { createContext, useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useToast } from "../components/ui/use-toast"
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, updateProfile } from "firebase/auth"
import { auth } from "../firebase/config"
import { removeNotificationToken, getCurrentDeviceToken } from "../utils/notification-token"
import apiClient from "../api/apiClient"
import { nativePostMessage } from "../utils/native-messaging"

export interface User {
  id: string
  email: string
  fullName: string
  phoneNumber: string
  building: string
  roomNumber: string
  profilePhotoUrl: string
  role: "user" | "admin"
  isApproved: boolean
  isDisabled?: boolean
  canReceiveMessages?: boolean
  muteGuestNotifications?: boolean
  disableEmailNotifications?: boolean
  managedBuildings?: string[]
  managedTicketCategories?: string[]
  isSuperAdmin?: boolean
  createdAt: string
  notificationTokens?: string[]
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  error: string | null
  login: (email: string, password: string, saveToken?: boolean) => Promise<void>
  register: (userData: RegisterData) => Promise<void>
  logout: () => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  isAuthenticated: boolean
  clearError: () => void
}

interface RegisterData {
  email: string
  password: string
  fullName: string
  phoneNumber: string
  building: string
  roomNumber: string
  registrationCode: string
  profilePhoto: File | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()

  // Initialize user from localStorage on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = localStorage.getItem("user")
        if (storedUser) {
          const userData = JSON.parse(storedUser)
          setUser(userData)

          // Set up API client authorization if we have a current user in Firebase
          if (auth.currentUser) {
            try {
              const idToken = await auth.currentUser.getIdToken()
              apiClient.defaults.headers.common["Authorization"] = `Bearer ${idToken}`
            } catch (error) {
              console.error("Error getting ID token on init:", error)
              // Clear stored user if token is invalid
              localStorage.removeItem("user")
              setUser(null)
            }
          }
        }
      } catch (error) {
        console.error("Error loading user from localStorage:", error)
        localStorage.removeItem("user")
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()
  }, [])

  // Set up API client authorization header when user is loaded from localStorage
  useEffect(() => {
    if (user && auth.currentUser) {
      auth.currentUser
        .getIdToken()
        .then((idToken) => {
          apiClient.defaults.headers.common["Authorization"] = `Bearer ${idToken}`
        })
        .catch((error) => {
          console.error("Error getting ID token:", error)
        })
    }
  }, [user])

  const clearError = () => {
    setError(null)
  }

  const login = async (email: string, password: string, saveToken = true) => {
    setIsLoading(true)
    setError(null)

    try {
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      const firebaseUser = userCredential.user

      // Set the authorization header
      const idToken = await firebaseUser.getIdToken()
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${idToken}`

      // Call the login API endpoint to validate on the server side and get user data
      try {
        const response = await apiClient.post("/auth/login", {
          email: email,
          uid: firebaseUser.uid,
        })

        const userData = response.data.user

        // Check if user is approved
        if (!userData.isApproved) {
          const errorMessage = "Your account is pending approval by an administrator."
          setError(errorMessage)
          toast({
            title: "Account not approved",
            description: errorMessage,
            variant: "destructive",
          })
          await signOut(auth)
          navigate("/auth/pending-approval")
          throw new Error(errorMessage)
        }

        const userWithId = {
          ...userData,
          id: firebaseUser.uid,
        }

        setUser(userWithId)
        // Store user in localStorage
        localStorage.setItem("user", JSON.stringify(userWithId))

        // Send native message on successful login if saveToken is true
        if (saveToken) {
          nativePostMessage("login|" + email + "|" + password)
        }

        toast({
          title: "Login successful",
          description: "Welcome back to Unity Smart Living!",
        })

        navigate("/dashboard")
      } catch (apiError: any) {
        // If the API rejects the login, sign out and throw error
        await signOut(auth)
        const errorMessage =
          apiError.response?.data?.message || apiError.response?.data?.error || "Login failed on server"
        setError(errorMessage)
        throw new Error(errorMessage)
      }
    } catch (error: any) {
      console.error("Login error:", error)

      // Handle Firebase Auth errors with user-friendly messages
      let errorMessage = "An unexpected error occurred. Please try again."

      switch (error.code) {
        case "auth/user-not-found":
          errorMessage = "No account found with this email address. Please check your email or create a new account."
          break
        case "auth/wrong-password":
          errorMessage = "Incorrect password. Please try again."
          break
        case "auth/invalid-email":
          errorMessage = "Please enter a valid email address."
          break
        case "auth/user-disabled":
          errorMessage = "This account has been disabled. Please contact support."
          break
        case "auth/too-many-requests":
          errorMessage = "Too many failed login attempts. Please try again later."
          break
        case "auth/network-request-failed":
          errorMessage = "Network error. Please check your internet connection and try again."
          break
        case "auth/invalid-credential":
          errorMessage = "Invalid email or password. Please check your credentials and try again."
          break
        default:
          errorMessage = error.message || "Login failed. Please check your email and password and try again."
      }

      setError(errorMessage)
      toast({
        title: "Login failed",
        description: errorMessage,
        variant: "destructive",
      })
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (userData: RegisterData) => {
    setIsLoading(true)
    setError(null)

    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, userData.email, userData.password)
      const firebaseUser = userCredential.user

      // Update profile display name
      await updateProfile(firebaseUser, {
        displayName: userData.fullName,
      })

      // Set the authorization header
      const idToken = await firebaseUser.getIdToken()
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${idToken}`

      // Prepare request data
      let requestData: any

      if (userData.profilePhoto) {
        // Create FormData for file upload
        const formData = new FormData()
        formData.append("email", userData.email)
        formData.append("fullName", userData.fullName)
        formData.append("phoneNumber", userData.phoneNumber || "")
        formData.append("building", userData.building)
        formData.append("roomNumber", userData.roomNumber)
        formData.append("registrationCode", userData.registrationCode)
        formData.append("uid", firebaseUser.uid)
        formData.append("profilePhoto", userData.profilePhoto)

        requestData = formData
      } else {
        // Regular JSON request
        requestData = {
          email: userData.email,
          fullName: userData.fullName,
          phoneNumber: userData.phoneNumber || "",
          building: userData.building,
          roomNumber: userData.roomNumber,
          registrationCode: userData.registrationCode,
          uid: firebaseUser.uid,
        }
      }

      // Call the register API endpoint
      try {
        const response = await apiClient.post("/auth/register", requestData, {
          headers: userData.profilePhoto
            ? { "Content-Type": "multipart/form-data" }
            : { "Content-Type": "application/json" },
        })

        // Update Firebase Auth profile with photo URL if it was uploaded
        if (response.data.user?.profilePhotoUrl) {
          await updateProfile(firebaseUser, {
            photoURL: response.data.user.profilePhotoUrl,
          })
        }

        toast({
          title: "Registration successful",
          description: "Your account is pending approval. You'll receive an email when approved.",
        })

        // Sign out after registration since account needs approval
        await signOut(auth)

        navigate("/auth/pending-approval")
      } catch (apiError: any) {
        // If the API registration fails, delete the Firebase Auth user
        await firebaseUser.delete()
        const errorMessage =
          apiError.response?.data?.error || apiError.response?.data?.message || "Registration failed on server"
        setError(errorMessage)
        throw new Error(errorMessage)
      }
    } catch (error: any) {
      console.error("Registration error:", error)

      // Handle Firebase Auth errors with user-friendly messages
      let errorMessage = "Registration failed. Please try again."

      switch (error.code) {
        case "auth/email-already-in-use":
          errorMessage =
            "An account with this email address already exists. Please use a different email or try logging in."
          break
        case "auth/invalid-email":
          errorMessage = "Please enter a valid email address."
          break
        case "auth/operation-not-allowed":
          errorMessage = "Email/password accounts are not enabled. Please contact support."
          break
        case "auth/weak-password":
          errorMessage = "Password is too weak. Please choose a stronger password with at least 6 characters."
          break
        case "auth/network-request-failed":
          errorMessage = "Network error. Please check your internet connection and try again."
          break
        default:
          errorMessage = error.message || "Registration failed. Please check your information and try again."
      }

      setError(errorMessage)
      toast({
        title: "Registration failed",
        description: errorMessage,
        variant: "destructive",
      })
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      // Get the current device's token
      const currentToken = getCurrentDeviceToken()

      // If we have a token for this device, remove it before logging out
      if (currentToken) {
        try {
          // We don't await this call to avoid blocking the logout process
          // if the token removal fails
          removeNotificationToken(currentToken).catch((error) => {
            console.error("Error removing notification token during logout:", error)
          })
        } catch (error) {
          console.error("Error removing notification token during logout:", error)
        }
      }

      // Send native message on successful logout
      nativePostMessage("logout")

      // Sign out from Firebase Auth
      await signOut(auth)
      setUser(null)
      setError(null)
      localStorage.removeItem("user")
      // Clear the authorization header
      delete apiClient.defaults.headers.common["Authorization"]
      navigate("/")
    } catch (error: any) {
      console.error("Logout error:", error)
      const errorMessage = error.message || "Logout failed"
      setError(errorMessage)
      throw error
    }
  }

  const forgotPassword = async (email: string) => {
    setIsLoading(true)
    setError(null)

    try {
      await apiClient.post("/auth/forgot-password", { email })

      toast({
        title: "Password reset email sent",
        description: "Check your email for instructions to reset your password",
      })

      navigate("/auth/login")
    } catch (error: any) {
      console.error("Password reset error:", error)

      // Handle API and Firebase Auth errors with user-friendly messages
      let errorMessage = "Failed to send password reset email. Please try again."

      // Check if it's a Firebase error from the API response
      const firebaseErrorCode = error.response?.data?.details || error.response?.data?.code

      if (firebaseErrorCode) {
        switch (firebaseErrorCode) {
          case "auth/user-not-found":
            errorMessage = "No account found with this email address. Please check your email or create a new account."
            break
          case "auth/invalid-email":
            errorMessage = "Please enter a valid email address."
            break
          case "auth/too-many-requests":
            errorMessage = "Too many password reset requests. Please try again later."
            break
          case "auth/network-request-failed":
            errorMessage = "Network error. Please check your internet connection and try again."
            break
          default:
            errorMessage = error.response?.data?.error || "Failed to send password reset email. Please try again."
        }
      } else {
        errorMessage = error.response?.data?.error || "Failed to send password reset email. Please try again."
      }

      setError(errorMessage)
      toast({
        title: "Password reset failed",
        description: errorMessage,
        variant: "destructive",
      })
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        login,
        register,
        logout,
        forgotPassword,
        isAuthenticated: !!user,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
